import React, { useState, useEffect, useCallback, useMemo } from 'react';
import Button from 'src/components/Button';
import Loader from 'src/components/Loader';
import RoomToggle from './RoomToggle/RoomToggle';
import PreconfiguredForm from './PreconfiguredForm/PreconfiguredForm';
import CustomRoomForm from './CustomRoomForm/CustomRoomForm';
import AddRoomDialogSkeleton from './skeletons/AddRoomDialogSkeleton';
import './AddRoomDialogue.scss';
import useSuiteAreas, {
  LookupArea,
} from 'src/SharedCustomHooks/UseSuiteAreas/useSuiteAreas';
import GenericDialogue from 'src/components/Dialogue/Dialogue';

export interface RoomOption {
  value: string;
  label: string;
}

const AddRoomDialog: React.FC<any> = ({
  isOpen,
  onClose,
  suiteId,
  userId,
  roleName,
}) => {
  const {
    lookupAreas,
    unitAreas,
    loading,
    submitting,
    error,
    clearError,
    fetchAreas,
    addPreconfiguredRoom,
    addCustomRoom,
  } = useSuiteAreas({ suiteId, userId, roleName });

  const [mode, setMode] = useState<'preconfigured' | 'custom'>('preconfigured');
  const [selectedAreaId, setSelectedAreaId] = useState('');
  const [customLabel, setCustomLabel] = useState('');
  const [customRoomName, setCustomRoomName] = useState('');

  useEffect(() => {
    if (isOpen) {
      fetchAreas();
      setMode('preconfigured');
      setSelectedAreaId('');
    }

    return () => {
      setMode('preconfigured');
      setSelectedAreaId('');
      setCustomLabel('');
      setCustomRoomName('');
      clearError();
    };
  }, [isOpen]);

  const options: RoomOption[] = useMemo(
    () =>
      lookupAreas.map((a: LookupArea) => ({
        value: a.id,
        label: unitAreas.some((ua: any) => ua.areaId === a.id)
          ? `${a.name} (added)`
          : a.name,
      })),
    [lookupAreas, unitAreas]
  );

  const handleConfirm = async () => {
    const success: boolean =
      mode === 'preconfigured'
        ? await addPreconfiguredRoom(selectedAreaId, customLabel || undefined)
        : await addCustomRoom(customRoomName);
    if (success) onClose();
  };

  const handleSelect = (option: { value: string; label: string } | null) => {
    setSelectedAreaId(option?.value || '');
    clearError();
  };

  return (
    <GenericDialogue
      isOpen={isOpen}
      onClose={onClose}
      title="Add a Room"
      maxWidth="100rem"
      maxHeight="150rem"
      footer={
        loading || submitting ? undefined : (
          <>
            <Button
              label="Cancel"
              onClick={onClose}
              disabled={submitting}
              size="small"
            />
            <Button
              color="teal"
              label="Confirm"
              onClick={handleConfirm}
              disabled={submitting}
              size="small"
            />
          </>
        )
      }
    >
      {loading ? (
        <AddRoomDialogSkeleton />
      ) : submitting ? (
        <Loader containerHeight="45.5rem" />
      ) : (
        <div className="dialog_body">
          <RoomToggle
            mode={mode}
            onChange={(m) => {
              setMode(m);
              clearError();
            }}
          />
          {mode === 'preconfigured' ? (
            <PreconfiguredForm
              options={options}
              selectedId={selectedAreaId}
              onSelect={handleSelect}
              labelValue={customLabel}
              onLabelChange={setCustomLabel}
            />
          ) : (
            <CustomRoomForm
              value={customRoomName}
              onChange={setCustomRoomName}
            />
          )}
        </div>
      )}
    </GenericDialogue>
  );
};

export default AddRoomDialog;
